/**
 * Geodesic voxel binding (GVB) weight solver: turns a `VertexDistanceCache` plus the user's
 * `SkinParams` into `SkinData`. Everything here is pure — no DOM, no editor state — and
 * deterministic: the same cache + params produce byte-identical typed arrays, which matters
 * because `SkinData` is persisted and because the UI re-solves on every slider change.
 *
 * Pipeline (per unique vertex position, then scattered to every seam duplicate):
 *   1. kernel  `w_b = exp(-(d'_b - dmin) / τ_b)` with `d'_b = max(0, d_b - offset_b)` and
 *      `dmin = min_b d'_b`; `τ_b` is that joint's `blend`, a transition width **in metres**
 *   2. unreachable shells fall back to Euclidean point-to-segment distances (or to `boneIds[0]`)
 *   3. hierarchy-neighbour rule: only the dominant bone, its parent and its children survive
 *   4. relative prune (ramped) → top 4 → normalize
 *   5. Laplacian smoothing over the unique-vertex mesh graph, restricted to each vertex's own
 *      hierarchy so a neighbouring limb cannot donate its bone back
 *   6. hierarchy rule once more, on the post-smoothing dominant → top 4 → normalize → write slots
 *      (strongest first, unused slots index 0 / weight 0)
 *   7. region overrides (weight 1 on the target bone, 2 smoothed boundary rings)
 *
 * Invariant upheld unconditionally: every vertex ends with finite weights summing to 1 (±1e-6).
 * `SolveStats.nonFinite` counts the vertices that needed the emergency repair to get there.
 */

import { AppError } from '../../shared/errors';
import type { CanonicalBoneId, SkinData, SkinParams, SkinRegionOverride } from '../../shared/types';
import type { BoneSegment } from '../skinning';
import { hierarchyNeighbours, jointParamsOf } from './skinParams';
import type { VertexAdjacency, VertexDistanceCache } from './types';

const SLOTS = 4; // three.js skin attributes are always 4 wide
const MIN_TAU = 1e-3; // metres; a blend width below this would quantize the falloff to a step
const TAU_CUTOFF = 20; // excess/τ beyond this contributes exp(-20) ≈ 2e-9 — flush to zero
const PRUNE_LOW = 0.01; // relative weights below this fraction of the row maximum are dropped
const PRUNE_HIGH = 0.02; // …and ramp linearly back to full strength up to here
const MAX_SMOOTHING_ITERATIONS = 50; // same sanity cap the params normalizer applies
// Boundary smoothing of a region override (no `SkinParams` available in that entry point).
const OVERRIDE_RINGS = 2;
const OVERRIDE_ITERATIONS = 2;
const OVERRIDE_LAMBDA = 0.5;

/** Euclidean rescue data for vertices the geodesic pass could not reach (separate shells). */
export interface SolveFallback {
  /** flat model-space xyz, indexed by vertex: `positions[v * 3 + k]` */
  positions: ArrayLike<number>;
  /** matched to `cache.boneIds` by `id`; bones without a segment stay unreachable */
  segments: BoneSegment[];
}

export interface SolveStats {
  /** unique vertices whose cached distances were non-finite for every bone */
  unreachable: number;
  /** vertices (not unique positions) that were solved from the Euclidean fallback */
  fallbackVertices: number;
  /** vertices whose weights had to be repaired to keep the sum-to-one invariant (0 in practice) */
  nonFinite: number;
  solveMs: number;
}

// ---------------------------------------------------------------------------
// Solve

/** GVB weights for one mesh. See `solveSkinWeightsDetailed` for the statistics. */
export function solveSkinWeights(
  cache: VertexDistanceCache, params: SkinParams,
  boneIndexOf: (id: CanonicalBoneId) => number, fallback?: SolveFallback,
): SkinData {
  return solveSkinWeightsDetailed(cache, params, boneIndexOf, fallback).skin;
}

/**
 * `solveSkinWeights` plus the diagnostics the UI and the leak tools want. Throws `AppError`
 * only for a caller mistake (empty cache, `boneIndexOf` that yields no skeleton index);
 * malformed distances are absorbed, never thrown.
 */
export function solveSkinWeightsDetailed(
  cache: VertexDistanceCache, params: SkinParams,
  boneIndexOf: (id: CanonicalBoneId) => number, fallback?: SolveFallback,
): { skin: SkinData; stats: SolveStats } {
  const started = performance.now();

  const boneCount = cache.boneIds.length;
  if (boneCount === 0) {
    throw new AppError('SKIN_NO_BONES', 'Cannot solve skin weights without bones.');
  }
  const uniqueCount = cache.uniqueCount;
  const vertexCount = cache.vertexCount;

  // Skeleton indices resolved once — `boneIndexOf` indexes the built Skeleton (Root = 0),
  // `cache.boneIds` excludes Root; the two index spaces must never be confused.
  const boneIndices = new Int32Array(boneCount);
  for (let b = 0; b < boneCount; b += 1) {
    const id = cache.boneIds[b];
    const index = boneIndexOf(id);
    if (!Number.isInteger(index) || index < 0) {
      const message = `No skeleton bone index for canonical bone "${id}".`;
      throw new AppError('SKIN_INVALID_BONE_INDEX', message, { bone: id, index });
    }
    boneIndices[b] = index;
  }

  const offsets = new Float64Array(boneCount);
  const taus = new Float64Array(boneCount);
  for (let b = 0; b < boneCount; b += 1) {
    const jp = jointParamsOf(params, cache.boneIds[b]);
    offsets[b] = jp.offset;
    taus[b] = Math.max(MIN_TAU, Number.isFinite(jp.blend) ? jp.blend : MIN_TAU);
  }

  // --- 1. kernel: exponential falloff over the *excess* distance ----------
  // Pass one finds the closest bone of each unique vertex, pass two measures every other bone
  // against it, so `blend` is a real transition length in metres and the mesh extent is irrelevant.
  const weights = new Float32Array(uniqueCount * boneCount);
  const nearest = new Float32Array(uniqueCount).fill(Infinity);
  for (let b = 0; b < boneCount; b += 1) {
    const distanceBase = b * uniqueCount;
    const offset = offsets[b];
    for (let u = 0; u < uniqueCount; u += 1) {
      const d = cache.distances[distanceBase + u];
      if (!Number.isFinite(d)) continue; // Infinity or NaN → no influence
      const effective = d > offset ? d - offset : 0;
      if (effective < nearest[u]) nearest[u] = effective;
    }
  }
  for (let b = 0; b < boneCount; b += 1) {
    const distanceBase = b * uniqueCount;
    const offset = offsets[b];
    const tau = taus[b];
    for (let u = 0; u < uniqueCount; u += 1) {
      const dmin = nearest[u];
      if (dmin === Infinity) continue; // unreachable, handled below
      const d = cache.distances[distanceBase + u];
      if (!Number.isFinite(d)) continue;
      const effective = d > offset ? d - offset : 0;
      weights[u * boneCount + b] = falloff(effective, dmin, tau);
    }
  }

  // --- 2. unreachable shells --------------------------------------------
  const stats: SolveStats = { unreachable: 0, fallbackVertices: 0, nonFinite: 0, solveMs: 0 };
  const segments = fallback ? matchSegments(cache.boneIds, fallback.segments) : null;
  const rescue = segments ? new Float64Array(boneCount) : null;
  for (let u = 0; u < uniqueCount; u += 1) {
    if (nearest[u] !== Infinity) continue;
    stats.unreachable += 1;
    const base = u * boneCount;
    const first = firstVertexOfUnique(cache.verticesOfUnique, u);
    if (segments && rescue && first >= 0 && first < vertexCount) {
      const p = fallback!.positions;
      const px = num(p[first * 3]);
      const py = num(p[first * 3 + 1]);
      const pz = num(p[first * 3 + 2]);
      let dmin = Infinity;
      for (let b = 0; b < boneCount; b += 1) {
        const segment = segments[b];
        let effective = Infinity;
        if (segment) {
          const d = distanceToSegment(px, py, pz, segment);
          if (Number.isFinite(d)) effective = d > offsets[b] ? d - offsets[b] : 0;
        }
        rescue[b] = effective;
        if (effective < dmin) dmin = effective;
      }
      if (dmin < Infinity) {
        for (let b = 0; b < boneCount; b += 1) {
          if (rescue[b] < Infinity) weights[base + b] = falloff(rescue[b], dmin, taus[b]);
        }
      } else {
        weights[base] = 1;
      }
      const vo = cache.verticesOfUnique;
      stats.fallbackVertices += Math.max(0, vo.offsets[u + 1] - vo.offsets[u]);
    } else {
      // no rescue data: park the shell on the first cached bone (Hips) and keep going
      weights[base] = 1;
    }
  }

  // --- 3./4. hierarchy rule, prune, top-4, normalize ---------------------
  const allowed = hierarchyMask(cache.boneIds);
  const slotBone = new Int32Array(SLOTS);
  const slotWeight = new Float64Array(SLOTS);
  const taken = new Uint8Array(boneCount);
  const dominantOf = new Int32Array(uniqueCount).fill(-1);
  for (let u = 0; u < uniqueCount; u += 1) {
    const base = u * boneCount;
    let dominant = -1;
    let max = 0;
    for (let b = 0; b < boneCount; b += 1) {
      const w = weights[base + b];
      if (w > max) {
        max = w;
        dominant = b;
      }
    }
    if (dominant < 0) continue; // all-zero row — repaired when the slots are written
    dominantOf[u] = dominant;
    const maskBase = dominant * boneCount;
    const low = max * PRUNE_LOW;
    const span = max * PRUNE_HIGH - low;
    for (let b = 0; b < boneCount; b += 1) {
      const w = weights[base + b];
      if (w <= 0) continue;
      if (allowed[maskBase + b] === 0 || w < low) weights[base + b] = 0;
      else if (span > 0 && w < low + span) weights[base + b] = w * ((w - low) / span);
    }
    collapseRow(weights, base, boneCount, slotBone, slotWeight, taken);
  }

  // --- 5. Laplacian smoothing -------------------------------------------
  const iterations = clampInt(params.smoothingIterations, 0, MAX_SMOOTHING_ITERATIONS);
  const lambda = clamp01(params.smoothingLambda);
  let current = weights;
  if (iterations > 0 && lambda > 0) {
    let scratch = new Float32Array(uniqueCount * boneCount);
    for (let i = 0; i < iterations; i += 1) {
      smoothOnce(current, scratch, cache.adjacency, uniqueCount, boneCount, lambda, dominantOf, allowed);
      const swap = current;
      current = scratch;
      scratch = swap;
    }
  }

  // --- 6. hierarchy rule again (post-smoothing argmax), top-4, normalize --
  // Smoothing pulls a neighbour's bones in, and on a mesh where the arms touch the torso those
  // neighbours are arm vertices. Re-applying the rule on the *smoothed* dominant is what keeps an
  // UpperArm channel out of a Hips-dominant torso vertex.
  for (let u = 0; u < uniqueCount; u += 1) {
    const base = u * boneCount;
    let dominant = -1;
    let max = 0;
    for (let b = 0; b < boneCount; b += 1) {
      const w = current[base + b];
      if (w > max) {
        max = w;
        dominant = b;
      }
    }
    if (dominant < 0) continue;
    const maskBase = dominant * boneCount;
    for (let b = 0; b < boneCount; b += 1) {
      if (allowed[maskBase + b] === 0) current[base + b] = 0;
    }
  }

  // --- 7. scatter to duplicates -----------------------------------------
  const skinIndex = new Uint16Array(vertexCount * SLOTS);
  const skinWeight = new Float32Array(vertexCount * SLOTS);
  const verticesOfUnique = cache.verticesOfUnique;
  const repairBone = boneIndices[0];
  for (let u = 0; u < uniqueCount; u += 1) {
    const count = collapseRow(current, u * boneCount, boneCount, slotBone, slotWeight, taken);
    const start = verticesOfUnique.offsets[u];
    const end = verticesOfUnique.offsets[u + 1];
    for (let k = start; k < end; k += 1) {
      const v = verticesOfUnique.neighbors[k];
      if (v < 0 || v >= vertexCount) continue;
      const slot = v * SLOTS;
      if (count === 0) {
        skinIndex[slot] = repairBone;
        skinWeight[slot] = 1;
        stats.nonFinite += 1;
        continue;
      }
      for (let i = 0; i < count; i += 1) {
        skinIndex[slot + i] = boneIndices[slotBone[i]];
        skinWeight[slot + i] = slotWeight[i];
      }
    }
  }

  let skin: SkinData = { meshName: cache.meshName, vertexCount, skinIndex, skinWeight };

  // --- 7. region overrides ----------------------------------------------
  const overrides = params.regionOverrides;
  if (overrides && overrides.length > 0) {
    skin = applyRegionOverrides(skin, cache, overrides, boneIndexOf);
  }

  stats.solveMs = performance.now() - started;
  return { skin, stats };
}

// ---------------------------------------------------------------------------
// Queries

/** Bone index in slot 0 — the strongest influence. `-1` for an out-of-range vertex. */
export function dominantBone(skin: SkinData, vertex: number): number {
  if (!Number.isInteger(vertex) || vertex < 0 || vertex >= skin.vertexCount) return -1;
  return skin.skinIndex[vertex * SLOTS];
}

/**
 * Every vertex index (seam duplicates included, ascending) of the connected component — over
 * `cache.adjacency` — of unique vertices whose dominant bone equals the clicked vertex's.
 * This is the "region" a one-click reassignment acts on.
 */
export function regionOfVertex(cache: VertexDistanceCache, skin: SkinData, vertex: number): number[] {
  const out: number[] = [];
  if (!Number.isInteger(vertex) || vertex < 0) return out;
  if (vertex >= skin.vertexCount || vertex >= cache.vertexCount) return out;
  const target = dominantBone(skin, vertex);
  if (target < 0) return out;
  const seed = cache.uniqueOf[vertex];
  if (seed < 0 || seed >= cache.uniqueCount) return out;

  const visited = new Uint8Array(cache.uniqueCount);
  const queue: number[] = [seed];
  visited[seed] = 1;
  const adjacency = cache.adjacency;
  const verticesOfUnique = cache.verticesOfUnique;
  for (let head = 0; head < queue.length; head += 1) {
    const u = queue[head];
    const vs = verticesOfUnique.offsets[u];
    const ve = verticesOfUnique.offsets[u + 1];
    for (let k = vs; k < ve; k += 1) {
      const v = verticesOfUnique.neighbors[k];
      if (v >= 0 && v < skin.vertexCount) out.push(v);
    }
    const ns = adjacency.offsets[u];
    const ne = adjacency.offsets[u + 1];
    for (let k = ns; k < ne; k += 1) {
      const n = adjacency.neighbors[k];
      if (n < 0 || n >= cache.uniqueCount || visited[n] === 1) continue;
      const fv = firstVertexOfUnique(verticesOfUnique, n);
      if (fv < 0 || fv >= skin.vertexCount || skin.skinIndex[fv * SLOTS] !== target) continue;
      visited[n] = 1;
      queue.push(n);
    }
  }
  out.sort((a, b) => a - b);
  return out;
}

// ---------------------------------------------------------------------------
// Region overrides

/**
 * Pin whole regions to one bone. Every listed vertex (and every seam duplicate of it) gets
 * weight 1 on `boneIndexOf(override.boneId)`; the two rings of unique vertices around the
 * region are then Laplacian-smoothed (2 iterations, lambda 0.5) with the region interior held
 * fixed, so the boundary is not a hard edge. Overrides for another mesh, for `Root`, or with
 * out-of-range vertices are ignored. Returns a NEW `SkinData`.
 */
export function applyRegionOverrides(
  skin: SkinData, cache: VertexDistanceCache, overrides: SkinRegionOverride[],
  boneIndexOf: (id: CanonicalBoneId) => number,
): SkinData {
  const out: SkinData = {
    meshName: skin.meshName, vertexCount: skin.vertexCount,
    skinIndex: skin.skinIndex.slice(), skinWeight: skin.skinWeight.slice(),
  };
  if (!overrides || overrides.length === 0) return out;

  for (const override of overrides) {
    if (!override || override.meshName !== cache.meshName) continue;
    if (override.boneId === 'Root') continue;
    const boneIndex = boneIndexOf(override.boneId);
    if (!Number.isInteger(boneIndex) || boneIndex < 0) continue;
    applyOneOverride(out, cache, override.vertices, boneIndex);
  }
  return out;
}

function applyOneOverride(
  skin: SkinData, cache: VertexDistanceCache, vertices: number[], boneIndex: number,
): void {
  const uniqueCount = cache.uniqueCount;
  const inRegion = new Uint8Array(uniqueCount);
  const region: number[] = [];
  for (const v of vertices) {
    if (!Number.isInteger(v) || v < 0 || v >= skin.vertexCount || v >= cache.vertexCount) continue;
    const u = cache.uniqueOf[v];
    if (u < 0 || u >= uniqueCount || inRegion[u] === 1) continue;
    inRegion[u] = 1;
    region.push(u);
  }
  if (region.length === 0) return;

  for (const u of region) writeSlotsForUnique(skin, cache, u, [[boneIndex, 1]], boneIndex);

  // rings: unique vertices adjacent to the region but outside it, then their neighbours
  const adjacency = cache.adjacency;
  const ringFlag = new Uint8Array(uniqueCount);
  const ring: number[] = [];
  let frontier = region;
  for (let r = 0; r < OVERRIDE_RINGS; r += 1) {
    const next: number[] = [];
    for (const u of frontier) {
      const s = adjacency.offsets[u];
      const e = adjacency.offsets[u + 1];
      for (let k = s; k < e; k += 1) {
        const n = adjacency.neighbors[k];
        if (n < 0 || n >= uniqueCount || inRegion[n] === 1 || ringFlag[n] === 1) continue;
        ringFlag[n] = 1;
        ring.push(n);
        next.push(n);
      }
    }
    frontier = next;
  }
  if (ring.length === 0) return;

  // Jacobi smoothing on the ring only; everything else acts as a fixed boundary condition.
  const values = new Map<number, Map<number, number>>();
  const valueOf = (u: number): Map<number, number> => {
    let m = values.get(u);
    if (!m) values.set(u, (m = influenceMapOfUnique(skin, cache, u)));
    return m;
  };
  for (let it = 0; it < OVERRIDE_ITERATIONS; it += 1) {
    const next = new Map<number, Map<number, number>>();
    for (const u of ring) {
      const s = adjacency.offsets[u];
      const e = adjacency.offsets[u + 1];
      const acc = new Map<number, number>();
      let used = 0;
      for (let k = s; k < e; k += 1) {
        const n = adjacency.neighbors[k];
        if (n < 0 || n >= uniqueCount) continue;
        used += 1;
        for (const [bone, w] of valueOf(n)) acc.set(bone, (acc.get(bone) ?? 0) + w);
      }
      if (used === 0) continue;
      const blended = new Map<number, number>();
      for (const [bone, w] of valueOf(u)) blended.set(bone, (1 - OVERRIDE_LAMBDA) * w);
      const scale = OVERRIDE_LAMBDA / used;
      for (const [bone, w] of acc) blended.set(bone, (blended.get(bone) ?? 0) + w * scale);
      next.set(u, blended);
    }
    for (const [u, m] of next) values.set(u, m);
  }

  for (const u of ring) {
    const map = values.get(u);
    if (!map) continue;
    writeSlotsForUnique(skin, cache, u, [...map.entries()], boneIndex);
  }
}

// ---------------------------------------------------------------------------
// internals

/**
 * `w = exp(-(d' - dmin) / τ)`: the closest bone always scores 1 and a bone `τ` metres further
 * away scores 1/e, so `τ` (the user's "blend width") *is* the width of the transition band.
 * Excesses beyond `TAU_CUTOFF` τ are flushed to 0 — they are far below the prune threshold and
 * skipping the `Math.exp` there is what keeps a 50k-vertex solve in the tens of milliseconds.
 */
function falloff(effective: number, dmin: number, tau: number): number {
  const excess = effective - dmin;
  if (!(excess > 0)) return 1; // the nearest bone (and anything tied with it)
  const t = excess / tau;
  if (!(t < TAU_CUTOFF)) return 0;
  return Math.exp(-t);
}

/** `allowed[dominant * n + b] === 1` when bone `b` is `dominant`, its parent or one of its children. */
function hierarchyMask(boneIds: CanonicalBoneId[]): Uint8Array {
  const n = boneIds.length;
  const mask = new Uint8Array(n * n);
  for (let dominant = 0; dominant < n; dominant += 1) {
    const neighbours = new Set<string>(hierarchyNeighbours(boneIds[dominant]));
    const base = dominant * n;
    for (let b = 0; b < n; b += 1) if (neighbours.has(boneIds[b])) mask[base + b] = 1;
  }
  return mask;
}

/**
 * Keep the 4 strongest channels of one row, normalize them to 1 and write them back (every
 * other channel becomes 0). `slotBone`/`slotWeight` receive the kept channels strongest-first
 * (ties broken by ascending bone slot, so the result is deterministic). Returns how many were
 * kept — 0 means the row carried no usable weight at all and the caller must repair it.
 */
function collapseRow(
  row: Float32Array, base: number, boneCount: number,
  slotBone: Int32Array, slotWeight: Float64Array, taken: Uint8Array,
): number {
  for (let b = 0; b < boneCount; b += 1) taken[b] = 0;
  let count = 0;
  for (let slot = 0; slot < SLOTS; slot += 1) {
    let best = -1;
    let bestWeight = 0;
    for (let b = 0; b < boneCount; b += 1) {
      if (taken[b] === 1) continue;
      const w = row[base + b];
      if (w > bestWeight) {
        bestWeight = w;
        best = b;
      }
    }
    if (best < 0) break;
    taken[best] = 1;
    slotBone[count] = best;
    slotWeight[count] = bestWeight;
    count += 1;
  }

  let sum = 0;
  for (let i = 0; i < count; i += 1) sum += slotWeight[i];
  for (let b = 0; b < boneCount; b += 1) row[base + b] = 0;
  if (!(sum > 0) || !Number.isFinite(sum)) return 0;
  for (let i = 0; i < count; i += 1) {
    const w = slotWeight[i] / sum;
    slotWeight[i] = w;
    row[base + slotBone[i]] = w;
  }
  return count;
}

/**
 * One Jacobi pass of `w ← (1-λ)w + λ·mean(neighbours)` over every bone channel — but only over the
 * channels the vertex's own dominant bone is allowed to have. Without that mask a torso vertex
 * whose neighbours are arm vertices accumulates an arm channel here and keeps it.
 */
function smoothOnce(
  src: Float32Array, dst: Float32Array, adjacency: VertexAdjacency,
  uniqueCount: number, boneCount: number, lambda: number,
  dominantOf: Int32Array, allowed: Uint8Array,
): void {
  const keep = 1 - lambda;
  for (let u = 0; u < uniqueCount; u += 1) {
    const base = u * boneCount;
    const s = adjacency.offsets[u];
    const e = adjacency.offsets[u + 1];
    for (let b = 0; b < boneCount; b += 1) dst[base + b] = 0;
    let used = 0;
    for (let k = s; k < e; k += 1) {
      const n = adjacency.neighbors[k];
      if (n < 0 || n >= uniqueCount) continue;
      const nb = n * boneCount;
      for (let b = 0; b < boneCount; b += 1) dst[base + b] += src[nb + b];
      used += 1;
    }
    if (used === 0) {
      for (let b = 0; b < boneCount; b += 1) dst[base + b] = src[base + b];
      continue;
    }
    const scale = lambda / used;
    for (let b = 0; b < boneCount; b += 1) dst[base + b] = keep * src[base + b] + scale * dst[base + b];
    // drop whatever the neighbours contributed outside this vertex's own hierarchy
    const dominant = dominantOf[u];
    if (dominant < 0) continue;
    const maskBase = dominant * boneCount;
    for (let b = 0; b < boneCount; b += 1) if (allowed[maskBase + b] === 0) dst[base + b] = 0;
  }
}

function matchSegments(boneIds: CanonicalBoneId[], segments: BoneSegment[]): (BoneSegment | null)[] {
  const byId = new Map<string, BoneSegment>();
  for (const s of segments) if (s && !byId.has(s.id)) byId.set(s.id, s);
  return boneIds.map((id) => byId.get(id) ?? null);
}

/** Point-to-segment distance; local copy so the solver does not depend on the old skinner. */
function distanceToSegment(px: number, py: number, pz: number, segment: BoneSegment): number {
  const { head, tail } = segment;
  const dx = tail[0] - head[0];
  const dy = tail[1] - head[1];
  const dz = tail[2] - head[2];
  const lengthSq = dx * dx + dy * dy + dz * dz;
  let t = 0;
  if (lengthSq > 1e-12) {
    t = ((px - head[0]) * dx + (py - head[1]) * dy + (pz - head[2]) * dz) / lengthSq;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
  }
  return Math.hypot(px - (head[0] + dx * t), py - (head[1] + dy * t), pz - (head[2] + dz * t));
}

function influenceMapOfUnique(skin: SkinData, cache: VertexDistanceCache, u: number): Map<number, number> {
  const map = new Map<number, number>();
  const v = firstVertexOfUnique(cache.verticesOfUnique, u);
  if (v < 0 || v >= skin.vertexCount) return map;
  const base = v * SLOTS;
  for (let slot = 0; slot < SLOTS; slot += 1) {
    const w = skin.skinWeight[base + slot];
    const bone = skin.skinIndex[base + slot];
    if (w > 0) map.set(bone, (map.get(bone) ?? 0) + w);
  }
  return map;
}

/** Top-4 of `entries`, normalized to 1, written into every vertex of `unique`. */
function writeSlotsForUnique(
  skin: SkinData, cache: VertexDistanceCache, unique: number,
  entries: Array<[number, number]>, fallbackBone: number,
): void {
  const kept = entries
    .filter(([, w]) => w > 0 && Number.isFinite(w))
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .slice(0, SLOTS);
  let sum = 0;
  for (const [, w] of kept) sum += w;

  const verticesOfUnique = cache.verticesOfUnique;
  const start = verticesOfUnique.offsets[unique];
  const end = verticesOfUnique.offsets[unique + 1];
  for (let k = start; k < end; k += 1) {
    const v = verticesOfUnique.neighbors[k];
    if (v < 0 || v >= skin.vertexCount) continue;
    const base = v * SLOTS;
    skin.skinIndex.fill(0, base, base + SLOTS);
    skin.skinWeight.fill(0, base, base + SLOTS);
    if (!(sum > 0) || !Number.isFinite(sum)) {
      skin.skinIndex[base] = fallbackBone;
      skin.skinWeight[base] = 1;
      continue;
    }
    for (let slot = 0; slot < kept.length; slot += 1) {
      skin.skinIndex[base + slot] = kept[slot][0];
      skin.skinWeight[base + slot] = kept[slot][1] / sum;
    }
  }
}

function firstVertexOfUnique(verticesOfUnique: VertexAdjacency, unique: number): number {
  const start = verticesOfUnique.offsets[unique];
  if (start >= verticesOfUnique.offsets[unique + 1]) return -1;
  return verticesOfUnique.neighbors[start];
}

function num(value: number | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function clampInt(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, Math.round(value)));
}
